import React, { useMemo, useRef, useState } from 'react';

import ApproversTable from '../../user/ApproversTable';
import ApprovalOutcomeChip from '../myviews/ApprovalOutcomeChip';
import {
    actionedLabel,
    OUTCOME_PENDING,
    TYPE_BUDGET_CHANGE,
    TYPE_EXPENSE_FUND,
    TYPE_POSITION_FUND,
    TYPE_PURCHASE_ORDER,
    TYPE_PURCHASE_REQUISITION,
} from '../myviews/queueTypes';
import { ACTION_DONE, ACTION_IDLE, ACTION_INVALID, ACTION_SUBMITTING, isStaleState } from '../recordActionStates';

import BudgetChangeBody from './bodies/BudgetChangeBody';
import ExpenseFundBody from './bodies/ExpenseFundBody';
import PositionFundBody from './bodies/PositionFundBody';
import PurchaseOrderBody from './bodies/PurchaseOrderBody';
import PurchaseRequisitionBody from './bodies/PurchaseRequisitionBody';
import useApprovalAction from './useApprovalAction';

import Alert from 'CommonComponents/Alert';
import Button from 'CommonComponents/Button';
import FormSectionHeading from 'CommonComponents/FormSectionHeading';
import Modal from 'CommonComponents/Modal';
import ModalHeader from 'CommonComponents/ModalHeader';

/**
 * ONE pop-up for every row in Home's approvals register, replacing the eight
 * per-type modals this screen used to drive imperatively.
 *
 * WHAT IT DOES NOT DO: decide who may approve. The client never derives
 * authority — it offers the controls the server's row said to offer, and the
 * server refuses anything else. A refusal is a state here, not an error.
 *
 * FOUR MODES, all derived from the row rather than from local state:
 *
 *   history    — the user already decided; shows the outcome and their comment.
 *   actionable — pending AND can_act_inline; comment box, Approve, Reject.
 *   read-only  — pending but not inline; deep-links out. No type is in this
 *                mode today: budget changes joined the other four once IB-961
 *                made their source-of-funds split system-calculated, leaving
 *                nothing for the approver to decide but yes or no.
 *   refused    — the server declined the last attempt; message, no controls.
 *
 * THE COMMENT SHOWN ON A HISTORY ROW IS `row.comment`, never
 * `record.pivot.comments`. One record can carry several approval attempts and
 * the pivot that survives hydration belongs to whichever came back last, so the
 * pivot shows the wrong attempt's text. IB-1167 fixed exactly that.
 *
 * Comment text renders as React children throughout. Nothing here passes it to
 * dangerouslySetInnerHTML.
 */

const BODIES = {
    [TYPE_PURCHASE_REQUISITION]: PurchaseRequisitionBody,
    [TYPE_PURCHASE_ORDER]: PurchaseOrderBody,
    [TYPE_POSITION_FUND]: PositionFundBody,
    [TYPE_EXPENSE_FUND]: ExpenseFundBody,
    [TYPE_BUDGET_CHANGE]: BudgetChangeBody,
};

/**
 * `id` is a prop, not a constant, because Home keeps a visited tab MOUNTED
 * (HomeTabs renders on first visit and retains). Once a user has opened both the
 * Home tab and My approvals, the summary's pop-up and the register's pop-up are
 * in the DOM at the same time, and a shared id would give two elements the same
 * one — with the wrong one answering.
 */
export default function ApprovalDetailModal({ modal, row, myViews, onClose, onActioned, id = 'home-approval-detail' }) {
    const [comments, setComments] = useState('');
    const [extras, setExtras] = useState({});

    /* Forces the body to remount on every genuine open, including a reopen of
       the SAME row. The pop-up never unmounts between opens (see `lastRowRef`
       below), so a body keyed only on `record.id` cannot tell "still open"
       from "closed and reopened" and its fetch-on-open effect never refires —
       showing figures that may be stale the moment another request landed
       while the pop-up was shut. A fresh key each open also isolates any
       body's in-flight fetch from a later one: a slow response from a body
       already unmounted has nowhere to write. */
    const [bodyKey, setBodyKey] = useState(0);
    const [openRow, setOpenRow] = useState(null);

    // A success needs no acknowledgement: the pop-up closes and the register is
    // re-read. Every refusal stays open so the user reads why.
    const action = useApprovalAction({
        onSettled: (state) => state === ACTION_DONE && onActioned?.(),
    });

    /* The row is cleared the instant the pop-up starts closing, but flowbite is
       still animating it out — rendering the empty shell at that point flashes a
       blank box over the page. The last row is held so the content stays put
       until it is gone. */
    const lastRowRef = useRef(null);

    if (row) {
        lastRowRef.current = row;
    }

    const shownRow = row ?? lastRowRef.current;

    const type = shownRow?.type;
    const record = shownRow?.record ?? {};
    const pending = shownRow?.outcome === OUTCOME_PENDING;
    const actionable = pending && shownRow?.can_act_inline;

    /* A fresh row is a fresh decision: nothing the previous one collected —
       comment, recipients, totals, or a refusal message — may survive into it.

       RESET DURING RENDER, NOT IN AN EFFECT. React re-renders before committing,
       so the body mounts once, already under its new key. Reset in an effect,
       the body mounted under the old key, fetched, and remounted on the bump to
       fetch again — and the reset ran after the body's own mount effects, so it
       would wipe what the body had just reported (the seeded recipients). */
    const rowIdentity = row ? `${row.type}:${row.record?.id}` : null;

    if (rowIdentity !== openRow) {
        setOpenRow(rowIdentity);

        if (rowIdentity !== null) {
            setComments('');
            setExtras({});
            action.reset();
            setBodyKey((key) => key + 1);
        }
    }

    /* No fallback body. Every queue type has one, and defaulting an unknown type
       to some other type's body would render one record's detail under another
       record's header. */
    const Body = useMemo(() => BODIES[type] ?? null, [type]);

    const commentsId = `${id}-comments`;

    if (!shownRow) {
        return <Modal id={id} modal={modal} />;
    }

    const close = () => {
        // Every terminal state except a validation failure means the register on
        // screen is now wrong, so it is re-read rather than left stale.
        if (isStaleState(action.state)) {
            onActioned?.();
        } else {
            onClose?.();
        }
    };

    const act = (approve) =>
        action.submit(type, approve, {
            recordId: record.id,
            comments,
            statuses: type === TYPE_PURCHASE_ORDER ? myViews.approval_po_statuses : myViews.approval_pr_statuses,
            url: actionUrlFor(type, myViews),
            additionalRecipients: extras.additionalRecipients ?? [],
            totals: extras.totals ?? {},
        });

    // A budget change's and a purchase order's body render their own history, opening
    // with the submission — unless the detail failed to load, when this table is the
    // only history left.
    const bodyRendersHistory = (type === TYPE_BUDGET_CHANGE || type === TYPE_PURCHASE_ORDER) && !extras.totalsUnavailable;
    const approvers = bodyRendersHistory ? [] : record.approval_history || record.approvers || [];
    // The server withholds the link (null) from a user the Procurement dashboard would refuse.
    const openRecordUrl = type === TYPE_PURCHASE_ORDER || type === TYPE_PURCHASE_REQUISITION ? shownRow.href : null;

    const invalid = action.state === ACTION_INVALID;
    const refused = ![ACTION_IDLE, ACTION_SUBMITTING, ACTION_DONE].includes(action.state);
    const decidable = actionable && !extras.recordUnavailable;

    return (
        <Modal
            id={id}
            modal={modal}
            // A purchase order's four impact tiles need the wider frame to sit on one row.
            innerContainerClasses={type === TYPE_PURCHASE_ORDER ? '' : 'tw-max-w-3xl'}
            parentContainerClasses={type === TYPE_PURCHASE_ORDER ? 'modal__container-large' : ''}
        >
            <ModalHeader title={shownRow.type_label} subtitle={shownRow.title} close={close} />

            {!pending && (
                <div className="tw-mb-4 tw-flex tw-flex-col tw-gap-2">
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <ApprovalOutcomeChip outcome={shownRow.outcome} />
                        {shownRow.actioned_at && <span className="tw-text-xs tw-text-charcoal-60">{actionedLabel(shownRow.actioned_at)}</span>}
                    </div>
                    {shownRow.comment && <p className="tw-text-sm tw-text-charcoal">{shownRow.comment}</p>}
                </div>
            )}

            {Body && <Body key={bodyKey} row={shownRow} myViews={myViews} onExtrasChange={setExtras} readOnly={!actionable} />}

            {/* WHO ELSE HAS SIGNED OFF. Every per-type view modal this replaces
                showed this, and it is decision-relevant to the person about to
                approve — the acting user's own chip above says nothing about the
                rest of the chain. */}
            {approvers.length > 0 && (
                <div className="tw-mt-4">
                    <FormSectionHeading heading={$t('Approval History')} />
                    <div className="table__card">
                        <ApproversTable approvers={approvers} status={record.status} displayApprovalStatuses={myViews.display_approval_statuses} />
                    </div>
                </div>
            )}

            {decidable && (
                <div className="tw-mt-4 tw-flex tw-flex-col tw-gap-1.5">
                    <label className="text-input__label" htmlFor={commentsId}>
                        {$t('Comments')}
                    </label>
                    <textarea
                        className="tw-border-0 text-input"
                        id={commentsId}
                        name="comments"
                        value={comments}
                        placeholder={type === TYPE_PURCHASE_ORDER ? $t('Optional on approve. Required on reject.') : undefined}
                        onChange={(e) => setComments(e.target.value)}
                        aria-invalid={invalid}
                        aria-describedby={invalid ? `${commentsId}-error` : undefined}
                    />
                    {invalid && (
                        <p id={`${commentsId}-error`} role="alert" className="tw-text-xs tw-text-red">
                            {action.message}
                        </p>
                    )}
                </div>
            )}

            {refused && !invalid && (
                <div className="tw-mt-4">
                    <Alert type="error">{action.message}</Alert>
                </div>
            )}

            <div className="modal__footer">
                <Button variant="secondary" onClick={close}>
                    {$t('Close')}
                </Button>

                {openRecordUrl && (
                    <Button variant="outline" onClick={() => window.open(openRecordUrl, '_blank', 'noopener')}>
                        {$t('Open record')}
                    </Button>
                )}

                {/* The deep link SURVIVES the type becoming actionable here. The
                    budget change screen still carries the movements ledger and
                    the rest of the location's requests, which the pop-up does
                    not; what changed is that reaching it is no longer the only
                    way to decide. */}
                {type === TYPE_BUDGET_CHANGE && shownRow.href && (
                    <Button variant="outline" onClick={() => window.location.assign(shownRow.href)}>
                        {$t('Review in Budget')}
                    </Button>
                )}

                {/* Any type the server marks non-inline. None does today; kept so
                    a future one deep-links rather than silently offering nothing. */}
                {!actionable && pending && type !== TYPE_BUDGET_CHANGE && shownRow.href && (
                    <Button variant="primary" onClick={() => window.location.assign(shownRow.href)}>
                        {$t('Review in Budget')}
                    </Button>
                )}

                {/* A validation failure is CORRECTABLE, so the controls stay:
                    hiding them on a 422 tells the user what to fix and then
                    takes away the button that submits the fix. Every other
                    refusal is terminal and the controls go. */}
                {decidable && (!refused || invalid) && (
                    <>
                        {/* A rejection needs no figures, so it stays available
                            even when the body could not load them. */}
                        <Button variant="danger" disabled={action.submitting} onClick={() => act(false)}>
                            {$t('Reject')}
                        </Button>
                        {/* Approve is withheld when the totals are missing: the
                            server consumes them to compute the contingency draw,
                            so submitting fabricated zeros writes a wrong balance. */}
                        <Button variant="primary" disabled={action.submitting || extras.totalsUnavailable} onClick={() => act(true)}>
                            {$t('Approve')}
                        </Button>
                    </>
                )}
            </div>
        </Modal>
    );
}

const actionUrlFor = (type, myViews) =>
    ({
        [TYPE_PURCHASE_REQUISITION]: myViews.toggle_pr_url,
        [TYPE_PURCHASE_ORDER]: myViews.toggle_po_url,
        [TYPE_POSITION_FUND]: myViews.update_position_fund_approval_url,
        [TYPE_EXPENSE_FUND]: myViews.update_expense_fund_approval_url,
        [TYPE_BUDGET_CHANGE]: myViews.toggle_budget_change_url,
    })[type];
